import {
  ChevronDownIcon,
  ChevronUpIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
} from '@heroicons/react/16/solid';
import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/solid';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Loading, Modal, Tooltip } from 'react-daisyui';
import { randomString } from '../../../../functions/string.functions.ts';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';
import { useModalsContext } from '../../../../providers/ModalsProvider/Modals.context.ts';

import { useTranslation } from 'react-i18next';
import { NIL } from 'uuid';
import { showStatusEmoji } from '../../../../pages/MessengerV2/components/LeadRow/LeadRow.function.ts';
import { useSocketsContext } from '../../../../providers/SocketsProvider/Sockets.context.ts';
import {
  eChannelType,
  eLeadStatus,
} from '../../../../providers/SocketsProvider/Sockets.enums.ts';
import { tAdvanceSearchLead } from '../../../AdvanceSearch/AdvanceSearchModal.types.ts';
import { chatLeadParser } from '../../Chat.functions.ts';
import { useChatContext } from '../../Chat.hook.ts';
import FunnelModal from '../Modals/FunnelModal/FunnelModal.component.tsx';
import ChatSearchOpenButton from '../ChatSearch/ChatSearchOpenButton.component.tsx';
import useChatMenu from './ChatMenu.hook.ts';
import './ChatMenu.styles.scss';
import { ChatMessageContainerEnum, ChatState } from '../../Chat.enum.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { getModuleName } from '../../Chat.utils.ts';
import LeadCloseMenu from 'src/components/LeadClose/LeadCloseMenu.component';
import { useLeadClose } from 'src/components/LeadClose/useLeadClose.hook';
import { toCloseLeadRef } from 'src/components/LeadClose/LeadClose.functions';
import { useLocalStorageState } from 'ahooks';
import { tFunnelInfo, tLeadStages, tLeadStatuses } from '../../Chat.types.ts';

export default function ChatMenu() {
  const { trackEvent } = useAnalytics();
  // LEA-50: "Marcar resultado" sits after the status, only with `leads_lead-close`.
  const { enabled: leadCloseEnabled } = useLeadClose();
  const { selectedChat, getFunnelsWithStages, selectedMessageContainer } =
    useChatContext();
  const {
    handleUpdateCountMessages,
    handleUpdateArchive,
    handleUpdateStage,
    handleUpdateStatus,
    handleToggleBlock,
    handleConfirmBlock,
    isBlockModalOpen,
    setIsBlockModalOpen,
    isBlocked,
    loading,
    isActive,
  } = useChatMenu();
  const {
    changeFunnelModalIsOpen,
    setChangeFunnelModalIsOpen,
    selectedLeadForChatModal,
  } = useModalsContext();
  const { currentFunnel } = useBoardSlice();
  const [funnelData] = useLocalStorageState<tFunnelInfo[]>('funnelData', {
    defaultValue: [],
    listenStorageChange: true,
  });
  const currentFunnelLocalStorage = funnelData?.find(
    (funnel) => funnel.uuid === selectedLeadForChatModal?.funnel?.uuid,
  );
  const { leadUpdatedEvent } = useSocketsContext();
  const { t } = useTranslation('chat/chat');

  const [selectedLead, setSelectedLead] = useState<tAdvanceSearchLead | null>(
    null,
  );

  useEffect(() => {
    if (selectedLeadForChatModal) {
      setSelectedLead(selectedLeadForChatModal);
    }
  }, [selectedLeadForChatModal]);

  leadUpdatedEvent.useSubscription((lead) => {
    if (selectedLead?.id !== lead.response?.uuid) {
      return;
    }
    setSelectedLead((oldLead) => {
      if (!oldLead) return null;
      return chatLeadParser(lead, oldLead);
    });
  });

  // BUG-288 — the stage list and the funnel id written with it MUST come from
  // the same source. They used to be derived by two independent fallback
  // chains: the list preferred the lead's own funnel (`selectedChat.lead`)
  // while the write preferred the board's (`currentFunnel.id`). Open a lead
  // from advanced search while the board shows a different funnel and the
  // dropdown offered funnel A's stages under funnel B's id — leads-service
  // resolves the stage inside the funnel it is given, finds nothing, and
  // answers 404 on every attempt (0% success, never a transient).
  //
  // Each arm tests `.stages`, never its parent: the chain it replaced fell
  // through on a missing list (`currentFunnel?.stages || ...`), and the types
  // promising `stages` is always there do not hold for a `funnelData` entry
  // rehydrated from localStorage across a schema change. Stopping on a parent
  // whose list is absent would hand `stages: undefined` to `.map`/`.find`
  // below and blank the chat panel instead of degrading to the next source.
  const stageSource: { funnelId: string; stages: tLeadStages[] } =
    (selectedLead?.stage !== undefined &&
      selectedChat?.lead?.stages && {
        funnelId: selectedChat.lead.funnelId,
        stages: selectedChat.lead.stages,
      }) ||
      (currentFunnel?.stages && {
        funnelId: currentFunnel.id,
        stages: currentFunnel.stages,
      }) ||
      (currentFunnelLocalStorage?.stages && {
        funnelId: currentFunnelLocalStorage.uuid,
        stages: currentFunnelLocalStorage.stages,
      }) || { funnelId: '', stages: [] };

  const stages = stageSource.stages;
  const statusSource: { funnelId: string; statuses: tLeadStatuses[] } =
    (selectedChat?.lead?.statuses && {
      funnelId: selectedChat.lead.funnelId,
      statuses: selectedChat.lead.statuses,
    }) || { funnelId: '', statuses: [] };
  const statuses = statusSource.statuses;
  const hasUnreadMessages = (selectedLead?.chat?.unreadCount || 0) > 0;

  const getNameFromSelectedStage = () => {
    // TODO: Pass stage info if available to avoid rendering 'inbox' when the stage and funnel are null
    if (!selectedLead) return t('chatMenu.selectAnStage');

    if (
      selectedLead?.stage?.id === NIL ||
      selectedLead?.funnel?.uuid === NIL ||
      (!selectedLead?.stage && !selectedLead?.funnel)
    ) {
      return 'Inbox 📥';
    }

    // BUG-887 — three fallbacks, because the lookup above misses in two
    // distinct states and both used to render an empty `<p>`:
    //
    //   1. The race. On the first commit in Chat none of the `stageSource`
    //      arms has data yet — the chat API is in flight, `currentFunnel` is
    //      null off the Pipe board, and the `funnelData` localStorage entry
    //      may not have hydrated (Safari ITP evicts it, and ahooks' `storage`
    //      listener does not fire in the tab that wrote it). `stages` is `[]`.
    //
    //   2. The cross-funnel open (BUG-288 family). `stages` is populated but
    //      belongs to another funnel, so the lead's own stage is absent from
    //      it. A `stages.length` guard would not catch this one.
    //
    // The lead carries its stage name in its own payload, so prefer that over
    // a placeholder: during the race we render the real name instead of a
    // spinner, and it settles to the same string once the list arrives.
    // `||`, not `??` — the lead parsers in `Chat.functions`/`Chat.hook`
    // default a missing name to `''`, which `??` would happily render blank.
    const stage = stages.find(
      (stage: tLeadStages) => stage.id === selectedLead?.stage?.id,
    );
    return (
      stage?.name || selectedLead?.stage?.name || t('chatMenu.selectAnStage')
    );
  };

  const [isDropdownStageOpen, setIsDropdownStageOpen] =
    useState<boolean>(false);
  const [isDropdownStatusOpen, setIsDropdownStatusOpen] =
    useState<boolean>(false);

  const dropdownStageRef = useRef<HTMLDivElement>(null);
  const dropdownStatusRef = useRef<HTMLDivElement>(null);

  function toggleDropdownStage(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    setIsDropdownStageOpen(!isDropdownStageOpen);
  }

  function toggleDropdownStatus(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    setIsDropdownStatusOpen(!isDropdownStatusOpen);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownStageRef.current &&
        !dropdownStageRef.current.contains(event.target as Node)
      ) {
        setIsDropdownStageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownStatusRef.current &&
        !dropdownStatusRef.current.contains(event.target as Node)
      ) {
        setIsDropdownStatusOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  let statusName = selectedLead?.status?.name ?? 'NEUTRAL';
  statusName = statusName.toLowerCase();

  return (
    <div className="ChatMenu">
      <Tooltip message={t('chatMenu.changeStage')} position="bottom">
        <Dropdown
          className="LeadInfo__Info__Dropdown"
          vertical="bottom"
          horizontal="right"
          open={isDropdownStageOpen}
          ref={dropdownStageRef}
        >
          <Dropdown.Toggle
            size="sm"
            color="ghost"
            className="ChatMenu__DropDownToggle"
            onClick={toggleDropdownStage}
          >
            <div className="ChatMenu__DropDownToggle__Title">
              <p>{getNameFromSelectedStage()}</p>
              {isDropdownStageOpen ? (
                <ChevronUpIcon className="ChatMenu__DropDownToggle__Icon" />
              ) : (
                <ChevronDownIcon className="ChatMenu__DropDownToggle__Icon" />
              )}
            </div>
          </Dropdown.Toggle>
          {isDropdownStageOpen && (
            <Dropdown.Menu className="ChatMenu__DropDownMenu">
              {stages.map((stage: tLeadStages) => (
                <Dropdown.Item
                  onClick={(event) => {
                    handleUpdateStage(stage, stageSource.funnelId);
                    toggleDropdownStage(event);
                    trackEvent('front.chat.stage_btn.select', {
                      displayName: 'Seleccionar etapa',
                      description:
                        'Cuántos leads se cambian de etapa desde la conversación del lead',
                      ctaType: getModuleName(),
                    });
                  }}
                  key={randomString()}
                >
                  <p className="truncate">{stage.name}</p>
                </Dropdown.Item>
              ))}
              <Dropdown.Item
                onClick={async (event) => {
                  toggleDropdownStage(event);
                  setChangeFunnelModalIsOpen(!changeFunnelModalIsOpen);
                  trackEvent('front.chat.stage.funnel_btn.click', {
                    displayName: 'Seleccionar embudo',
                    description:
                      'Cuántos leads se cambian de embudo desde la conversación del lead',
                    ctaType: getModuleName(),
                  });
                  await getFunnelsWithStages();
                }}
                key={randomString()}
              >
                <p className="text-primary">{t('chatMenu.otherFunnel')}</p>
              </Dropdown.Item>
            </Dropdown.Menu>
          )}
        </Dropdown>
      </Tooltip>

      {!isEmpty(statuses) && (
        <Tooltip message={t('chatMenu.changeStatus')} position="bottom">
          <Dropdown
            className="LeadInfo__Info__Dropdown"
            vertical="bottom"
            horizontal="right"
            open={isDropdownStatusOpen}
            ref={dropdownStatusRef}
          >
            <Dropdown.Toggle
              size="sm"
              color="ghost"
              className="ChatMenu__DropDownToggle"
              onClick={toggleDropdownStatus}
            >
              <div className="ChatMenu__DropDownToggle__Title">
                <p>
                  {showStatusEmoji(
                    selectedLead?.status?.name ?? eLeadStatus.NEUTRAL,
                  )}
                  {selectedLead && t(`leadStatus.${statusName}`)}
                </p>
                {isDropdownStatusOpen ? (
                  <ChevronUpIcon className="ChatMenu__DropDownToggle__Icon" />
                ) : (
                  <ChevronDownIcon className="ChatMenu__DropDownToggle__Icon" />
                )}
              </div>
            </Dropdown.Toggle>
            {isDropdownStatusOpen && (
              <Dropdown.Menu className="ChatMenu__DropDownMenu">
                {statuses.map((status) => (
                  <Dropdown.Item
                    onClick={(event) => {
                      handleUpdateStatus({
                        stage: selectedLead?.stage || { id: '', name: '' },
                        status,
                        funnelId: statusSource.funnelId,
                      });
                      toggleDropdownStatus(event);
                      trackEvent('front.chat.estatus.select', {
                        displayName: 'Seleccionar estatus del lead',
                        description: 'Cuántos leads cambian de estatus',
                        statusType: status.name,
                      });
                      if (status.name === eLeadStatus.STUCK) {
                        // El nombre del evento se conserva tal cual: los
                        // tableros de PostHog dependen de el. Solo se corrigen
                        // las etiquetas, que describian configuracion de umbral
                        // cuando esto es un cambio manual de estatus de un lead.
                        trackEvent(
                          'front.funnels.stage.menu.stuck.save_btn.click',
                          {
                            displayName:
                              'Marcar un lead como atorado manualmente',
                            description:
                              'Cuantos leads reciben el estatus Atorado desde el menu del chat',
                          },
                        );
                      }
                    }}
                    key={randomString()}
                  >
                    {showStatusEmoji(status.name)}
                    {t('leadStatus.' + status.name.toLowerCase())}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            )}
          </Dropdown>
        </Tooltip>
      )}

      {leadCloseEnabled && selectedLead ? (
        <LeadCloseMenu lead={toCloseLeadRef(selectedLead)} />
      ) : null}

      <div className="ChatMenu__Buttons">
        <Tooltip
          message={
            hasUnreadMessages
              ? t('chatMenu.markAsRead')
              : t('chatMenu.markAsUnread')
          }
          position="bottom"
        >
          <Button
            onClick={() => {
              handleUpdateCountMessages(hasUnreadMessages);
            }}
            color="ghost"
            className="ChatMenu__Buttons__Box"
            variant="outline"
            size="sm"
          >
            {!hasUnreadMessages ? (
              <EnvelopeOpenIcon className="ChatMenu__Button__Icon" />
            ) : (
              <EnvelopeIcon className="ChatMenu__Button__Icon" />
            )}
          </Button>
        </Tooltip>

        <Tooltip
          message={isActive ? t('chatMenu.archive') : t('chatMenu.activate')}
          position="bottom"
        >
          <Button
            onClick={() => {
              handleUpdateArchive();
            }}
            color="ghost"
            variant="outline"
            className="ChatMenu__Buttons__Box"
            size="sm"
          >
            {isActive ? (
              <ArchiveBoxIcon className="ChatMenu__Button__Icon" />
            ) : (
              <ArchiveBoxArrowDownIcon className="ChatMenu__Button__Icon" />
            )}
          </Button>
        </Tooltip>

        {selectedLeadForChatModal?.chat?.integration ===
          eChannelType.WHATSAPP_API && (
          <Tooltip
            message={
              isBlocked
                ? t('chatMenu.unblock')
                : selectedChat?.state === ChatState.ACTIVE
                  ? t('chatMenu.block')
                  : t('chatMenu.blockDisabledInactive')
            }
            position="bottom"
          >
            <Button
              onClick={() => {
                handleToggleBlock();
              }}
              color="ghost"
              variant="outline"
              size="sm"
              disabled={!isBlocked && selectedChat?.state !== ChatState.ACTIVE}
              className={`ChatMenu__Buttons__Box text-error hover:bg-error hover:border-error hover:text-error-content disabled:text-error/40 ${
                isBlocked ? 'bg-error/10' : ''
              }`}
              aria-label={
                isBlocked ? t('chatMenu.unblock') : t('chatMenu.block')
              }
            >
              <NoSymbolIcon className="ChatMenu__Button__Icon" />
            </Button>
          </Tooltip>
        )}

        {/* SRE-3328: right after the block button. Renders nothing with
            useMessageSearchEnabled off (no ChatSearchProvider value), and only
            on the conversation (not on the Historial view), as it did in the
            header. */}
        {selectedMessageContainer === ChatMessageContainerEnum.CHAT ? (
          <ChatSearchOpenButton />
        ) : null}

        {loading && <Loading color="primary" />}
        {changeFunnelModalIsOpen && <FunnelModal />}
        <Modal
          open={isBlockModalOpen}
          backdrop
          onClose={() => setIsBlockModalOpen(false)}
        >
          <Modal.Header className="font-bold">
            {t('chatMenu.blockModal.title')}
          </Modal.Header>
          <Modal.Body>{t('chatMenu.blockModal.description')}</Modal.Body>
          <Modal.Actions>
            <Button
              color="ghost"
              size="sm"
              onClick={() => setIsBlockModalOpen(false)}
            >
              {t('chatMenu.blockModal.cancel')}
            </Button>
            <Button
              color="error"
              size="sm"
              onClick={() => {
                void handleConfirmBlock();
              }}
            >
              {t('chatMenu.blockModal.confirm')}
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
      <div className="w-full  flex justify-end">
        {selectedLeadForChatModal?.chat?.integration ===
        eChannelType.WHATSAPP_API ? (
          selectedChat?.state === ChatState.ACTIVE ? (
            <Tooltip message="" position="top" color="neutral">
              <div className="px-2 rounded-full bg-success flex text-base-100 items-center gap-2">
                <span className="rounded-full h-2 w-2 bg-base-100"></span>
                <div className="">{t('chatMenu.activeConnection')}</div>
              </div>
            </Tooltip>
          ) : (
            <div className="px-2 rounded-full bg-transparent border text-gray-400 border-gray-400 border-1 flex items-center gap-2">
              <span className="rounded-full h-2 w-2 bg-gray-400"></span>
              <div className="">{t('chatMenu.inactiveConnection')}</div>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
